// src/app/components/admin/registration-setting.tsx
//
// Platform section of Global Settings: whether STRANGERS may create their own
// account. POST /auth/register is unauthenticated, so with signup open anyone
// who can reach the API can mint an account and land on the login side of the
// product. Closed is the normal posture — accounts get made by an admin in
// User Management, which is also where they get their brand access.
//
// The value is stored on the backend (app_config, key='public_registration')
// and read on every registration attempt, so closing signup takes effect on
// the very next call. Admin-only; the backend enforces 403 on write and
// records every change in the auth audit log.

import { useEffect, useState } from "react";
import { Lock, UserPlus, Check, Loader2, AlertTriangle } from "lucide-react";

import { authApi } from "@/api/auth";

interface Choice {
  enabled: boolean;
  label: string;
  desc: string;
  icon: typeof Lock;
}

const CHOICES: Choice[] = [
  {
    enabled: false,
    label: "Closed — admin invitation only",
    desc:
      "Nobody can create their own account. New users are added in User Management, where you also give them brand access.",
    icon: Lock,
  },
  {
    enabled: true,
    label: "Open — anyone can sign up",
    desc:
      "Anyone who can reach the sign-in page may create an account. They arrive with no brand access until an admin assigns it.",
    icon: UserPlus,
  },
];

export function RegistrationSetting() {
  const [current, setCurrent] = useState<boolean | null>(null);
  const [saving, setSaving] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    authApi
      .getRegistrationSetting()
      .then((res) => alive && setCurrent(!!res.enabled))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function choose(enabled: boolean) {
    if (enabled === current || saving !== null) return;
    setSaving(enabled);
    setErr("");
    try {
      const stored = await authApi.setRegistrationSetting(enabled);
      setCurrent(!!stored.enabled);
      setSavedAt(Date.now());
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(null);
    }
  }

  const busy = loading || saving !== null;

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Self-service sign-up
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          Controls whether people outside your team can create their own account from the sign-in
          page. Takes effect immediately, on the next sign-up attempt. Every change to this setting
          is recorded in the audit log.
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}
          >
            {err}
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3">
          {CHOICES.map((choice) => {
            const Icon = choice.icon;
            const isCurrent = current === choice.enabled;
            const isSaving = saving === choice.enabled;
            return (
              <button
                key={String(choice.enabled)}
                type="button"
                disabled={busy}
                onClick={() => choose(choice.enabled)}
                className="flex items-start gap-3 rounded-xl border p-4 text-left transition-all"
                style={{
                  cursor: busy ? "default" : "pointer",
                  borderColor: isCurrent ? "var(--alpha-teal)" : "var(--border)",
                  backgroundColor: isCurrent ? "var(--alpha-teal-10)" : "var(--card, transparent)",
                  boxShadow: isCurrent ? "0 0 0 1px var(--alpha-teal)" : "none",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <span
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: isCurrent ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)", color: isCurrent ? "#fff" : "var(--muted-foreground)" }}
                >
                  <Icon className="h-4.5 w-4.5" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
                      {choice.label}
                    </span>
                    {isCurrent && !isSaving && (
                      <span className="inline-flex items-center gap-1" style={{ fontSize: "var(--text-xs)", color: "var(--alpha-teal)", fontWeight: "var(--font-weight-medium)" }}>
                        <Check className="h-3.5 w-3.5" /> Active
                      </span>
                    )}
                    {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--alpha-teal)" }} />}
                  </span>
                  <span className="mt-0.5 block" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                    {choice.desc}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Shown whenever sign-up is open, not just after a change — the state
            itself is what warrants the notice, however it got there. */}
        {current === true && !loading && (
          <div
            className="mt-4 flex items-start gap-2 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", backgroundColor: "var(--muted, #f1f5f9)" }}
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" style={{ marginTop: 2, color: "var(--chart-4, #d97706)" }} />
            <span>
              Sign-up is open. Anyone who can reach this deployment can create an account — review
              User Management regularly, and close this once your team is onboarded.
            </span>
          </div>
        )}

        {savedAt > 0 && saving === null && !err && (
          <p className="mt-3" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Saved. Sign-up is now {current ? "open to anyone" : "closed — admins add users"}.
          </p>
        )}
      </div>
    </div>
  );
}

export default RegistrationSetting;
